import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { HttpUtils } from '@core/utils/http-utils';
import { environment } from '@root/environments/environment';
import { map, Observable } from 'rxjs';
import { PriceRequestFilters } from '../store/price-request.state';
import {
  PriceRequest,
  PriceRequestApiModel,
  PriceRequestApiModelData,
  PriceRequestList,
} from '../types/pricing.types';

@Injectable({
  providedIn: 'root',
})
export class PriceRequestService {
  private http = inject(HttpClient);

  private readonly API_URL = `${environment.apiUrl}/nipt-invoicing`;

  public getPriceRequestsList(
    filters: PriceRequestFilters
  ): Observable<PriceRequestList> {
    return this.http
      .get<PriceRequestApiModel>(`${this.API_URL}/price-requests`, {
        params: this.buildPriceRequestHttpParams(filters),
      })
      .pipe(
        map(
          (model: PriceRequestApiModel): PriceRequestList => ({
            data: model.data.map((item) => this.apiModelToPriceRequest(item)),
            totalCount: model.total_count,
            hasMorePages: model.has_more_pages,
            perPage: model.per_page,
            currentPage: model.current_page,
          })
        )
      );
  }

  public approvePriceRequests(priceRequestIds: string[]): Observable<void> {
    return this.http.patch<void>(`${this.API_URL}/price-requests/approve`, {
      price_request_ids: priceRequestIds,
    });
  }

  public deletePriceRequest(
    partnerId: string,
    requestId: string
  ): Observable<void> {
    return this.http.delete<void>(
      `${this.API_URL}/partners/${partnerId}/price-requests/${requestId}`
    );
  }

  private buildPriceRequestHttpParams(filters: PriceRequestFilters) {
    let httpParams = HttpUtils.buildHttpParams();

    if (filters.partnerId) {
      httpParams = httpParams.set('partner_id', filters.partnerId);
    }

    if (filters.countries?.length) {
      httpParams = httpParams.set('countries', filters.countries.join(','));
    }

    if (filters.limit) {
      httpParams = httpParams.set('limit', filters.limit);
    }

    if (filters.page) {
      httpParams = httpParams.set('page', filters.page);
    }

    if (filters.status) {
      httpParams = httpParams.set('status', filters.status);
    }

    return httpParams;
  }

  private apiModelToPriceRequest(
    model: PriceRequestApiModelData
  ): PriceRequest {
    return {
      id: model.id,
      partnerId: model.partner_id,
      bundleIds: model.bundle_ids?.length
        ? model.bundle_ids
        : model.bundle_id
          ? [model.bundle_id]
          : [],
      price:
        model.price !== undefined && model.price !== null
          ? Number(model.price)
          : null,
      validFrom: model.valid_from ? new Date(model.valid_from) : new Date(0),
      createdBy: model.created_by ?? null,
      createdAt: model.created_at ? new Date(model.created_at) : null,
      currency: model.partner?.currency ?? model.currency ?? null,
      status: model.status ?? model.last_status ?? null,
      partner: model.partner
        ? {
            id: model.partner.id,
            name: model.partner.name,
            accountManagerId: model.partner.account_manager_id ?? null,
            country: model.partner.country,
            email: model.partner.email,
            phone: model.partner.phone,
            currency: model.partner.currency ?? null,
            notes: model.partner.notes ?? null,
            missingPrices: model.partner.missing_prices ?? null,
            active: model.partner.active,
            pricelist: [],
            priceRequests: [],
            paymentTypes: model.partner.payment_types ?? [],
          }
        : null,
    };
  }
}
